import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MONTH_NAMES } from '../constants/month-options';

export class DateValidators {
  static monthDayRange(
    monthFromKey: string,
    monthToKey: string,
    dayFromKey: string,
    dayToKey: string,
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!(group instanceof FormGroup)) {
        return null;
      }

      const monthFromControl = group.get(monthFromKey);
      const monthToControl = group.get(monthToKey);
      const dayFromControl = group.get(dayFromKey);
      const dayToControl = group.get(dayToKey);

      const monthFrom = DateValidators.#toMonthOrder(monthFromControl?.value);
      const monthTo = DateValidators.#toMonthOrder(monthToControl?.value);
      const dayFrom = DateValidators.#toNullableNumber(dayFromControl?.value);
      const dayTo = DateValidators.#toNullableNumber(dayToControl?.value);

      const isMonthRangeInvalid =
        monthFrom !== null && monthTo !== null && monthFrom > monthTo;
      const isDayFromInvalid = DateValidators.#isInvalidDayForMonth(
        dayFrom,
        monthFrom,
      );
      const isDayToInvalid = DateValidators.#isInvalidDayForMonth(
        dayTo,
        monthTo,
      );
      const isDayRangeInvalid = DateValidators.#isDayRangeInvalid({
        monthFrom,
        monthTo,
        dayFrom,
        dayTo,
      });

      DateValidators.#setControlError(
        monthFromControl,
        'rangeInvalid',
        isMonthRangeInvalid,
      );
      DateValidators.#setControlError(
        monthToControl,
        'rangeInvalid',
        isMonthRangeInvalid,
      );
      DateValidators.#setControlError(
        dayFromControl,
        'invalidDayForMonth',
        isDayFromInvalid,
      );
      DateValidators.#setControlError(
        dayToControl,
        'invalidDayForMonth',
        isDayToInvalid,
      );
      DateValidators.#setControlError(
        dayFromControl,
        'rangeInvalid',
        isDayRangeInvalid,
      );
      DateValidators.#setControlError(
        dayToControl,
        'rangeInvalid',
        isDayRangeInvalid,
      );

      return isMonthRangeInvalid
        ? { rangeInvalid: true }
        : isDayRangeInvalid
          ? { rangeInvalid: true }
          : isDayFromInvalid || isDayToInvalid
            ? { invalidDayForMonth: true }
            : null;
    };
  }

  static notInPast(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as Date | null;

      if (!value) {
        return null;
      }

      const now = new Date();
      now.setSeconds(0, 0);

      return value.getTime() < now.getTime() ? { dateInPast: true } : null;
    };
  }

  static notInFuture(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as Date | null;

      if (!value) {
        return null;
      }

      const today = new Date();

      return value.getTime() > today.getTime() ? { dateInFuture: true } : null;
    };
  }

  static startNotAfterEnd(
    startKey: string,
    endKey: string,
    errorKey = 'dateRangeInvalid',
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      if (!(group instanceof FormGroup)) {
        return null;
      }

      const start = group.get(startKey)?.value as Date | null;
      const end = group.get(endKey)?.value as Date | null;

      const endControl = group.get(endKey);
      const endErrors = { ...(endControl?.errors ?? {}) };

      if (start && end && start.getTime() > end.getTime()) {
        endControl?.setErrors({ ...endErrors, [errorKey]: true });
        return { [errorKey]: true };
      }

      if (endControl?.errors?.[errorKey]) {
        delete endErrors[errorKey];
        const hasErrors = Object.keys(endErrors).length > 0;
        endControl.setErrors(hasErrors ? endErrors : null);
      }

      return null;
    };
  }

  static #setControlError(
    control: AbstractControl | null,
    key: string,
    shouldSetError: boolean,
  ): void {
    if (!control) {
      return;
    }

    const errors = control.errors ?? {};

    if (shouldSetError) {
      control.setErrors({ ...errors, [key]: true });
      return;
    }

    if (!(key in errors)) {
      return;
    }

    const { [key]: _removed, ...remainingErrors } = errors;
    control.setErrors(
      Object.keys(remainingErrors).length ? remainingErrors : null,
    );
  }

  static #toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  static #toMonthOrder(value: unknown): number | null {
    if (typeof value !== 'string') {
      return null;
    }

    const monthIndex = MONTH_NAMES.indexOf(
      value as (typeof MONTH_NAMES)[number],
    );

    return monthIndex >= 0 ? monthIndex + 1 : null;
  }

  static #isInvalidDayForMonth(
    day: number | null,
    month: number | null,
  ): boolean {
    if (day === null || month === null) {
      return false;
    }

    const maxDaysPerMonth: Record<number, number> = {
      1: 31,
      2: 29,
      3: 31,
      4: 30,
      5: 31,
      6: 30,
      7: 31,
      8: 31,
      9: 30,
      10: 31,
      11: 30,
      12: 31,
    };

    return day > (maxDaysPerMonth[month] ?? 31);
  }

  static #isDayRangeInvalid(range: {
    monthFrom: number | null;
    monthTo: number | null;
    dayFrom: number | null;
    dayTo: number | null;
  }): boolean {
    const { monthFrom, monthTo, dayFrom, dayTo } = range;

    if (dayFrom === null || dayTo === null) {
      return false;
    }

    if (monthFrom !== null && monthTo !== null) {
      if (monthFrom > monthTo || monthFrom < monthTo) {
        return false;
      }
    }

    return dayFrom > dayTo;
  }
}
